/**
 * Fuzzy Search Options
 * 
 * This module provides Fuse.js configurations for different search scenarios.
 * Import FUZZY_OPTIONS for the full options object, or destructure specific options.
 */


/**
 * Token separator pattern for splitting search queries.
 * Matches: whitespace, commas, periods, tabs, parentheses, brackets, 
 * semicolons, colons, quotes, and other common separators.
 */
// eslint-disable-next-line no-useless-escape
export const TOKEN_SEPARATOR = /[\s,.\t()\[\]{};:'"!#$%^&*`~\/\\|<>?]+/;

/**
 * Core Fuse.js defaults - minimal settings shared across all FuzzySearcher usages.
 * These are automatically spread with any options passed to the constructor.
 */
export const FUSE_CORE_DEFAULTS = {
	shouldSort: true,
	isCaseSensitive: false,
	includeScore: true,
	threshold: 0.4,
	minMatchCharLength: 1
};

/**
 * Legacy defaults for backward compatibility with existing screens.
 * Used by screens like users, proxies, exports, entities that do simple filtering.
 */
export const FUSE_DATA_DEFAULTS = {
	...FUSE_CORE_DEFAULTS,
	keys: [
		"Name",
		"Code"
	]
};

/**
 * Entity search configuration - keys with getFn extractors for complex entity structures.
 * Treats top-level and nested entity properties with equal priority within each category.
 * 
 * Entity structure:
 * - Code (always present)
 * - Name (optional, when different from Code)
 * - ExternalKey (optional)
 * - Identifiers[].Value (optional, excluding TOKEN type)
 * - Entities[].Entity.{Code, Name, ExternalKey, Identifiers[]} (nested, same structure)
 */
const ENTITY_FUSE_KEYS = [
	// All codes from entity and nested entities - highest priority
	{
		name: "Code",
		weight: 0.40,
		getFn: (item) => {
			const codes = [];
			if(item.Code) codes.push(item.Code);
			if(item.Entities) {
				item.Entities.forEach(e => {
					if(e.Entity?.Code) codes.push(e.Entity.Code);
				});
			}
			return codes;
		}
	},

	// All names (excluding any that match codes) - high priority
	{
		name: "Name",
		weight: 0.30,
		getFn: (item) => {
			// Collect all codes to filter out matching names
			const allCodes = new Set();
			if(item.Code) allCodes.add(item.Code.toUpperCase());
			if(item.Entities) {
				item.Entities.forEach(e => {
					if(e.Entity?.Code) allCodes.add(e.Entity.Code.toUpperCase());
				});
			}

			const names = [];
			if(item.Name && !allCodes.has(item.Name.toUpperCase())) {
				names.push(item.Name);
			}
			if(item.Entities) {
				item.Entities.forEach(e => {
					if(e.Entity?.Name && !allCodes.has(e.Entity.Name.toUpperCase())) {
						names.push(e.Entity.Name);
					}
				});
			}
			//tokenize the names for better multi-word search results
			return (names)
				.filter(Boolean)
			;
		}
	},

	// All identifiers from entity and nested entities
	{
		name: "Identifiers",
		weight: 0.20,
		getFn: (item) => {
			const identifiers = [];
			// Own identifiers
			if(item.Identifiers) {
				item.Identifiers
					.filter(i => i.Type?.Code !== "TOKEN" && i.Value)
					.forEach(i => identifiers.push(i.Value));
			}
			// Nested identifiers
			if(item.Entities) {
				item.Entities.forEach(e => {
					const nestedIds = e.Entity?.Identifiers || [];
					nestedIds
						.filter(i => i.Type?.Code !== "TOKEN" && i.Value)
						.forEach(i => identifiers.push(i.Value));
				});
			}
			return identifiers;
		}
	},

	// All external keys
	{
		name: "ExternalKey",
		weight: 0.10,
		getFn: (item) => {
			const keys = [];
			if(item.ExternalKey) keys.push(item.ExternalKey);
			if(item.Entities) {
				item.Entities.forEach(e => {
					if(e.Entity?.ExternalKey) keys.push(e.Entity.ExternalKey);
				});
			}
			return keys;
		}
	}
];

/**
 * Entity search Fuse.js options - complete configuration for entity searching.
 * Combines core defaults with entity-specific keys and settings optimized for entity search.
 * 
 * Note: threshold is set higher (0.6) to allow more candidates through for multi-token search.
 * The FuzzySearcher class handles prioritization via result reweighting.
 * useExtendedSearch is intentionally omitted - it can cause issues with exact matching of long strings.
 */
const FUSE_ENTITY_OPTIONS = {
	...FUSE_CORE_DEFAULTS,
	threshold: 0.6,
	ignoreLocation: true,
	ignoreFieldNorm: true,
	keys: ENTITY_FUSE_KEYS
};

/**
 * User search configuration - searches by Name and Username.
 * Uses getFn to extract the full name from Contact object structure.
 */
const FUSE_USERS_OPTIONS = {
	...FUSE_CORE_DEFAULTS,
	threshold: 0.3,
	keys: [
		{
			name: "Name",
			weight: 0.5,
			getFn: (user) => user.Name ? user.Name.split(TOKEN_SEPARATOR).filter(Boolean) : []
		},
		{
			name: "Username",
			weight: 0.5,
			getFn: (user) => user.Username ? [
				user.Username, //include the users' full email
				user.Username.split("@")[0], //include the part before the @ for better search results
				...user.Username.split("@")[0]?.split(TOKEN_SEPARATOR) //include the part before the @, tokenized for better search results
			].filter(Boolean) : []
		}
	]
};

/**
 * Proxy search configuration - searches by Name only.
 */
const FUSE_PROXIES_OPTIONS = {
	...FUSE_CORE_DEFAULTS,
	threshold: 0.3,
	keys: [
		{
			name: "Name",
			weight: 1.0,
			getFn: (proxy) => proxy.Name ? proxy.Name.split(TOKEN_SEPARATOR).filter(Boolean) : []
		}
	]
};

/**
 * Export search configuration - searches by Id and Configuration name/code.
 */
const FUSE_EXPORTS_OPTIONS = {
	...FUSE_CORE_DEFAULTS,
	threshold: 0.4,
	distance: 32,
	keys: [
		{
			name: "Id",
			weight: 0.6,
			getFn: (exportItem) => exportItem.Id ? [exportItem.Id.toString()] : []
		},
		{
			name: "Configuration",
			weight: 0.4,
			getFn: (exportItem) => {
				const values = [];
				if(exportItem.Configuration?.Name) values.push(exportItem.Configuration.Name);
				if(exportItem.Configuration?.Code) values.push(exportItem.Configuration.Code);
				return values;
			}
		}
	]
};

/**
 * Notices search configuration - searches by Title and Content.
 */
const FUSE_NOTICES_OPTIONS = {
	...FUSE_CORE_DEFAULTS,
	threshold: 0.3,
	keys: [
		{
			name: "Title",
			weight: 0.6,
			getFn: (notice) => notice.Title ? notice.Title.split(TOKEN_SEPARATOR).filter(Boolean) : []
		},
		{
			name: "Content",
			weight: 0.2,
			getFn: (notice) => notice.Content ? notice.Content.split(TOKEN_SEPARATOR).filter(Boolean) : []
		},
		{
			name: "Severity",
			weight: 0.2,
			getFn: (notice) => notice.Severity?.Name ? [notice.Severity.Name] : []
		}
	]
};

/**
 * Full fuzzy options object containing all available configurations.
 * 
 * @property {Object} CORE - Core defaults with Name/Code keys (for simple filtering)
 * @property {Object} ENTITY - Entity-specific options with nested structure support
 * @property {Object} USERS - User search (Name, Username)
 * @property {Object} PROXIES - Proxy search (Name only)
 * @property {Object} EXPORTS - Export search (Id, Configuration)
 * @property {Object} NOTICES - Notice search (Title, Content, Severity)
 */
export const FUZZY_OPTIONS = {
	CORE: FUSE_CORE_DEFAULTS,
	DATA: FUSE_DATA_DEFAULTS,
	ENTITY: FUSE_ENTITY_OPTIONS,
	USERS: FUSE_USERS_OPTIONS,
	PROXIES: FUSE_PROXIES_OPTIONS,
	EXPORTS: FUSE_EXPORTS_OPTIONS,
	NOTICES: FUSE_NOTICES_OPTIONS
};

export default FUZZY_OPTIONS;
